<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import TicketsAPI from 'dashboard/api/tickets.js';
import { Bar, Pie } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { t } = useI18n();
const store = useStore();

// State
const isLoading = ref(false);
const fetchError = ref(null);
const stats = ref({});
const charts = ref([]);
const showAddChartModal = ref(false);
const showShareModal = ref(false);

// Filters
const dateRange = ref({
  start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split('T')[0],
  end: new Date().toISOString().split('T')[0],
});
const statusFilter = ref('all');
const priorityFilter = ref('all');

// New chart config
const newChart = ref({
  field: '',
  type: 'bar',
  title: '',
});

// Chart colors
const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
];

// Field labels
const FIELD_LABELS = {
  status: 'Estado',
  priority: 'Prioridad',
  ticket_category: 'Tipo de Ticket',
  ticket_subcategory: 'Tipo de Servicio',
  subcategory_tipo: 'Tipo',
  ticket_type: 'Tipo (legado)',
  channel: 'Canal',
  assignee: 'Asignado a',
};

const availableFields = [
  'status',
  'priority',
  'ticket_category',
  'ticket_subcategory',
  'subcategory_tipo',
  'ticket_type',
  'channel',
  'assignee',
];

// Methods
const fetchTickets = async () => {
  isLoading.value = true;
  fetchError.value = null;
  try {
    const response = await TicketsAPI.reportStats({
      start_date: dateRange.value.start,
      end_date: dateRange.value.end,
    });
    stats.value = response.data;
  } catch (error) {
    console.error('Error fetching report stats:', error);
    fetchError.value = error?.response?.status
      ? `Error ${error.response.status}: ${error.response?.data?.error || error.message}`
      : error.message;
  } finally {
    isLoading.value = false;
  }
};

const FIELD_TO_STAT = {
  status: 'by_status',
  priority: 'by_priority',
  channel: 'by_channel',
  ticket_category: 'by_category',
  ticket_subcategory: 'by_subcategory',
  assignee: 'by_assignee',
};

const generateChartData = field => {
  const statKey = FIELD_TO_STAT[field];
  const counts = statKey ? (stats.value[statKey] || {}) : {};

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
};

const getChartConfig = chart => {
  const data = generateChartData(chart.field);

  if (chart.type === 'bar') {
    return {
      labels: data.map(d => d.name),
      datasets: [
        {
          label: chart.title,
          data: data.map(d => d.value),
          backgroundColor: COLORS.slice(0, data.length),
        },
      ],
    };
  }
  return {
    labels: data.map(d => d.name),
    datasets: [
      {
        data: data.map(d => d.value),
        backgroundColor: COLORS.slice(0, data.length),
      },
    ],
  };
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
};

const handleAddChart = () => {
  if (!newChart.value.field) return;

  const chartTitle = newChart.value.title || FIELD_LABELS[newChart.value.field];

  charts.value.push({
    id: `chart-${Date.now()}`,
    field: newChart.value.field,
    type: newChart.value.type,
    title: chartTitle,
  });

  newChart.value = { field: '', type: 'bar', title: '' };
  showAddChartModal.value = false;
};

const handleRemoveChart = chartId => {
  charts.value = charts.value.filter(c => c.id !== chartId);
};

const downloadPDF = async () => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;

    // Header
    pdf.setFontSize(20);
    pdf.text('Reporte de Tickets', margin, 20);

    pdf.setFontSize(11);
    pdf.text(`Fecha: ${new Date().toLocaleDateString()}`, margin, 30);
    pdf.text(
      `Período: ${dateRange.value.start} - ${dateRange.value.end}`,
      margin,
      37
    );
    pdf.text(`Total de tickets: ${stats.value.total ?? 0}`, margin, 44);

    // Charts
    for (let i = 0; i < charts.value.length; i++) {
      const chart = charts.value[i];
      const chartElement = document.getElementById(`chart-${chart.id}`);

      if (chartElement) {
        if (i > 0) pdf.addPage();

        pdf.setFontSize(14);
        pdf.text(chart.title, margin, 60);

        const canvas = await html2canvas(chartElement, {
          scale: 2,
          backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth - margin * 2;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', margin, 70, imgWidth, imgHeight);
      }
    }

    pdf.save(`reporte_tickets_${Date.now()}.pdf`);
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el PDF');
  }
};

const copyShareLink = () => {
  const reportConfig = {
    charts: charts.value.map(c => ({
      field: c.field,
      type: c.type,
      title: c.title,
    })),
    filters: {
      dateRange: dateRange.value,
      status: statusFilter.value,
      priority: priorityFilter.value,
    },
  };

  const configString = btoa(JSON.stringify(reportConfig));
  const shareUrl = `${window.location.origin}${window.location.pathname}?config=${configString}`;

  navigator.clipboard
    .writeText(shareUrl)
    .then(() => {
      alert('Enlace copiado al portapapeles');
    })
    .catch(() => {
      alert('Error al copiar el enlace');
    });
};

const shareByEmail = () => {
  const subject = encodeURIComponent('Reporte de Tickets');
  const body = encodeURIComponent(
    `Reporte de Tickets\n\n` +
      `Período: ${dateRange.value.start} - ${dateRange.value.end}\n` +
      `Total de tickets: ${stats.value.total ?? 0}\n` +
      `Gráficas incluidas: ${charts.value.length}`
  );

  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};

const clearFilters = () => {
  dateRange.value = {
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0],
    end: new Date().toISOString().split('T')[0],
  };
  statusFilter.value = 'all';
  priorityFilter.value = 'all';
};

// Gráficas por default
const DEFAULT_CHARTS = [
  { id: 'default-status',      field: 'status',             type: 'bar', title: 'Por Estado' },
  { id: 'default-priority',    field: 'priority',           type: 'pie', title: 'Por Prioridad' },
  { id: 'default-category',    field: 'ticket_category',    type: 'bar', title: 'Por Categoría' },
  { id: 'default-subcategory', field: 'ticket_subcategory', type: 'bar', title: 'Por Tipo de Servicio' },
  { id: 'default-channel',     field: 'channel',            type: 'pie', title: 'Por Canal' },
  { id: 'default-assignee',    field: 'assignee',           type: 'bar', title: 'Por Agente' },
];

// LocalStorage persistence
const STORAGE_KEY = 'ticket_reports_state';

const saveToLocalStorage = () => {
  const state = {
    charts: charts.value,
    dateRange: dateRange.value,
    statusFilter: statusFilter.value,
    priorityFilter: priorityFilter.value,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const today = () => new Date().toISOString().split('T')[0];

const loadFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      charts.value = state.charts?.length ? state.charts : [...DEFAULT_CHARTS];
      if (state.dateRange) {
        // Always use today as end date so newly created tickets are always included
        dateRange.value = { ...state.dateRange, end: today() };
      }
      if (state.statusFilter) statusFilter.value = state.statusFilter;
      if (state.priorityFilter) priorityFilter.value = state.priorityFilter;
    } else {
      charts.value = [...DEFAULT_CHARTS];
    }
  } catch {
    charts.value = [...DEFAULT_CHARTS];
  }
};

// Lifecycle
onMounted(() => {
  loadFromLocalStorage();
  fetchTickets();

  // Register watchers AFTER initial load so loadFromLocalStorage doesn't trigger a double fetch
  watch(charts, saveToLocalStorage, { deep: true });
  watch(dateRange, () => { saveToLocalStorage(); fetchTickets(); }, { deep: true });
  watch(statusFilter, saveToLocalStorage);
  watch(priorityFilter, saveToLocalStorage);
});
</script>

<template>
  <div
    class="flex flex-col w-full h-full min-h-0 overflow-auto p-6 gap-6 bg-n-background"
  >
    <!-- Filters Card -->
    <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
      <div class="flex items-center gap-2 mb-4">
        <span class="i-lucide-filter text-n-slate-11" />
        <h3 class="font-semibold text-n-slate-12">Filtros de Datos</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Fecha Inicio</label>
          <input
            v-model="dateRange.start"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          />
        </div>
        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Fecha Fin</label>
          <input
            v-model="dateRange.end"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Estado</label>
          <select
            v-model="statusFilter"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          >
            <option value="all">Todos los estados</option>
            <option value="open">Abierto</option>
            <option value="in_progress">En Proceso</option>
            <option value="resolved">Resuelto</option>
            <option value="closed">Cerrado</option>
          </select>
        </div>
        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Prioridad</label>
          <select
            v-model="priorityFilter"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          >
            <option value="all">Todas las prioridades</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>

      <div
        class="flex items-center justify-between pt-4 border-t border-n-weak"
      >
        <div class="text-sm">
          <span v-if="isLoading" class="text-n-slate-11">Cargando tickets...</span>
          <span v-else-if="fetchError" class="text-n-ruby-11">{{ fetchError }}</span>
          <span v-else class="text-n-slate-11">Total: {{ stats.total ?? 0 }} tickets</span>
        </div>
        <div class="flex gap-2">
          <Button variant="outline" size="sm" @click="clearFilters">
            <span class="i-lucide-x mr-2" />
            Limpiar Filtros
          </Button>
          <Button variant="outline" size="sm" @click="fetchTickets">
            Actualizar Datos
          </Button>
        </div>
      </div>
    </div>

    <!-- Actions Bar -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-bold text-n-slate-12">
          Gráficas Personalizadas
        </h2>
        <p class="text-sm text-n-slate-11">
          {{
            charts.length === 0
              ? 'Agrega gráficas para visualizar tus datos'
              : `${charts.length} gráfica${charts.length > 1 ? 's' : ''} agregada${charts.length > 1 ? 's' : ''}`
          }}
        </p>
      </div>
      <div class="flex gap-2">
        <Button
          variant="outline"
          :disabled="charts.length === 0"
          @click="showShareModal = true"
        >
          <span class="i-lucide-share-2 mr-2" />
          Compartir
        </Button>
        <Button
          variant="outline"
          :disabled="charts.length === 0"
          @click="downloadPDF"
        >
          <span class="i-lucide-download mr-2" />
          Exportar PDF
        </Button>
        <Button color="blue" @click="showAddChartModal = true">
          <span class="i-lucide-plus mr-2" />
          Agregar Gráfica
        </Button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner :size="32" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="charts.length === 0"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-2 rounded-lg border border-n-weak"
    >
      <span class="i-lucide-bar-chart-3 text-6xl text-n-slate-10 mb-4" />
      <h3 class="text-xl font-semibold text-n-slate-12 mb-2">
        No hay gráficas agregadas
      </h3>
      <p class="text-n-slate-11 mb-6">
        Haz clic en "Agregar Gráfica" para comenzar a visualizar tus datos
      </p>
      <Button color="blue" @click="showAddChartModal = true">
        <span class="i-lucide-plus mr-2" />
        Agregar Primera Gráfica
      </Button>
    </div>

    <!-- Charts Grid -->
    <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div
        v-for="chart in charts"
        :key="chart.id"
        class="p-4 rounded-lg bg-n-solid-2 border border-n-weak"
      >
        <div class="flex items-start justify-between mb-4">
          <div>
            <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
              <span
                :class="
                  chart.type === 'bar'
                    ? 'i-lucide-bar-chart-3'
                    : 'i-lucide-pie-chart'
                "
              />
              {{ chart.title }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{
                chart.type === 'bar' ? 'Gráfico de barras' : 'Gráfico de pastel'
              }}
              -
              {{
                generateChartData(chart.field).reduce(
                  (sum, item) => sum + item.value,
                  0
                )
              }}
              tickets
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            @click="handleRemoveChart(chart.id)"
          >
            <span class="i-lucide-trash-2 text-n-ruby-11" />
          </Button>
        </div>

        <div :id="`chart-${chart.id}`" class="w-full h-[400px] mb-4">
          <Bar
            v-if="chart.type === 'bar'"
            :data="getChartConfig(chart)"
            :options="chartOptions"
          />
          <Pie v-else :data="getChartConfig(chart)" :options="chartOptions" />
        </div>

        <!-- Summary Table -->
        <div class="border border-n-weak rounded-lg overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-n-solid-3">
              <tr>
                <th class="text-left p-2 text-n-slate-11">
                  {{ FIELD_LABELS[chart.field] }}
                </th>
                <th class="text-right p-2 text-n-slate-11">Cantidad</th>
                <th class="text-right p-2 text-n-slate-11">%</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in generateChartData(chart.field)"
                :key="index"
                class="border-t border-n-weak"
              >
                <td class="p-2 text-n-slate-12">{{ item.name }}</td>
                <td class="text-right p-2 text-n-slate-12">{{ item.value }}</td>
                <td class="text-right p-2 text-n-slate-11">
                  {{
                    (
                      (item.value /
                        generateChartData(chart.field).reduce(
                          (sum, i) => sum + i.value,
                          0
                        )) *
                      100
                    ).toFixed(1)
                  }}%
                </td>
              </tr>
              <tr class="border-t border-n-weak font-bold bg-n-solid-3">
                <td class="p-2 text-n-slate-12">Total</td>
                <td class="text-right p-2 text-n-slate-12">
                  {{
                    generateChartData(chart.field).reduce(
                      (sum, item) => sum + item.value,
                      0
                    )
                  }}
                </td>
                <td class="text-right p-2 text-n-slate-12">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add Chart Modal -->
    <div
      v-if="showAddChartModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showAddChartModal = false"
    >
      <div
        class="bg-n-solid-1 rounded-lg p-6 w-full max-w-md border border-n-weak"
      >
        <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
          Agregar Nueva Gráfica
        </h3>

        <div class="space-y-4">
          <div>
            <label class="text-sm font-medium text-n-slate-11 mb-1 block">Campo a Analizar</label>
            <select
              v-model="newChart.field"
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            >
              <option value="">Selecciona un campo</option>
              <option
                v-for="field in availableFields"
                :key="field"
                :value="field"
              >
                {{ FIELD_LABELS[field] }}
              </option>
            </select>
          </div>

          <div>
            <label class="text-sm font-medium text-n-slate-11 mb-1 block">Tipo de Gráfico</label>
            <select
              v-model="newChart.type"
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            >
              <option value="bar">Gráfico de Barras</option>
              <option value="pie">Gráfico de Pastel</option>
            </select>
          </div>

          <div>
            <label class="text-sm font-medium text-n-slate-11 mb-1 block">Título (Opcional)</label>
            <input
              v-model="newChart.title"
              type="text"
              :placeholder="
                newChart.field
                  ? `Análisis de ${FIELD_LABELS[newChart.field]}`
                  : 'Título personalizado'
              "
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            />
          </div>
        </div>

        <div class="flex gap-2 mt-6">
          <Button
            variant="outline"
            class="flex-1"
            @click="
              () => {
                newChart = { field: '', type: 'bar', title: '' };
                showAddChartModal = false;
              }
            "
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            class="flex-1"
            :disabled="!newChart.field"
            @click="handleAddChart"
          >
            <span class="i-lucide-plus mr-2" />
            Agregar
          </Button>
        </div>
      </div>
    </div>

    <!-- Share Modal -->
    <div
      v-if="showShareModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showShareModal = false"
    >
      <div
        class="bg-n-solid-1 rounded-lg p-6 w-full max-w-md border border-n-weak"
      >
        <h3
          class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
        >
          <span class="i-lucide-share-2" />
          Compartir Reporte
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          Comparte este reporte con tu equipo
        </p>

        <div class="space-y-3 mb-4">
          <h4 class="text-sm font-medium text-n-slate-12">
            Información del Reporte
          </h4>
          <div class="text-sm text-n-slate-11 space-y-1">
            <p>• Período: {{ dateRange.start }} - {{ dateRange.end }}</p>
            <p>• Total de tickets: {{ stats.total ?? 0 }}</p>
            <p>• Gráficas incluidas: {{ charts.length }}</p>
          </div>
        </div>

        <div class="space-y-2">
          <Button
            variant="outline"
            class="w-full justify-start"
            @click="copyShareLink"
          >
            <span class="i-lucide-link mr-2" />
            Copiar enlace del reporte
          </Button>
          <Button
            variant="outline"
            class="w-full justify-start"
            @click="shareByEmail"
          >
            <span class="i-lucide-mail mr-2" />
            Compartir por email
          </Button>
          <Button
            variant="outline"
            class="w-full justify-start"
            @click="
              () => {
                showShareModal = false;
                downloadPDF();
              }
            "
          >
            <span class="i-lucide-download mr-2" />
            Exportar a PDF y compartir
          </Button>
        </div>

        <Button
          variant="outline"
          class="w-full mt-4"
          @click="showShareModal = false"
        >
          Cerrar
        </Button>
      </div>
    </div>
  </div>
</template>
